import { z } from 'zod';
import {
  demandFilterFieldsSchema,
  filterFieldsOf,
  type DemandFilterFields,
} from '../forecast/demand/demandFilters';

/**
 * The procurement filters a buyer carries between Forecast Summary, Forecast
 * Details, the buyer board (Outgoing Purchase Orders) and Purchase Order
 * Status.
 *
 * They live on one pathless route above those screens (./route.tsx), so
 * the filters are part of every one of their URLs and typed there once:
 *
 * - `procurementFilterSearchSchema` validates them for the whole group.
 * - `rememberFilters` is the group's search middleware. A navigation that does
 *   not say what a filter is gets the last value the buyer had; one that sets
 *   it — including to `undefined`, which is how a filter is cleared — keeps
 *   what it set. So moving between the screens (sidebar, links, Back) never
 *   resets anything, and clearing is always explicit.
 * - The group's `beforeLoad` records the filters of every URL it lands on.
 *
 * The record also covers arriving from outside the group — Accounts Payable,
 * then back through the sidebar — which URL retention alone cannot, since the
 * page you are leaving from has no procurement filters to retain. It lives in
 * sessionStorage: per tab (two tabs on two divisions do not fight), surviving
 * a reload, and cleared on logout and organization switch.
 *
 * One shape for every screen: the forecast grid's filter fields. Each screen
 * applies the fields that describe its rows and carries the rest untouched, so
 * they are still there on the way back:
 *
 * - Forecast Summary / Details: all of them.
 * - Buyer board: the location scope (`divisionIds`, `warehouseIds`, the same
 *   um_divisions / im_warehouses ids on both sides) plus the line filters in
 *   replenishment/utils/boardSharedFilters.ts (warehouse type, SKU / text
 *   search, stock status, POP code). The rest describe forecast series.
 * - Purchase Order Status: `divisionIds`.
 *
 * Screen-only filters (the board's buyer code, line type and readiness, PO
 * Status's status and dates) stay in each screen's own search params.
 */

/**
 * The board once wrote its ids as one comma-separated string; old links and
 * bookmarks still do. `.catch` rather than a preprocess so the schema's input
 * type stays `string[]` for every Link that builds these URLs: anything that is
 * not a valid list is read as the legacy form, and whatever of it is not a
 * uuid is dropped — one bad entry never drops the rest or fails the route.
 */
const UUID = z.uuid();

const idListSchema = z
  .array(z.uuid())
  .optional()
  .catch(({ value }) => legacyIdList(value));

function legacyIdList(value: unknown): string[] | undefined {
  const candidates =
    typeof value === 'string'
      ? value.split(',').map((id) => id.trim())
      : Array.isArray(value)
        ? value
        : [];
  const ids = candidates.filter(
    (id): id is string => UUID.safeParse(id).success,
  );
  return ids.length ? ids : undefined;
}

export const procurementFilterSearchSchema = demandFilterFieldsSchema.extend({
  divisionIds: idListSchema,
  warehouseIds: idListSchema,
});

export type ProcurementFilters = z.infer<typeof procurementFilterSearchSchema>;

const FILTER_FIELD_NAMES = demandFilterFieldsSchema.keyof().options;

/** TanStack's search-middleware shape (the type itself is not re-exported). */
type FilterMiddleware = (ctx: {
  search: ProcurementFilters;
  next: (search: ProcurementFilters) => ProcurementFilters;
}) => ProcurementFilters;

export const rememberFilters: FilterMiddleware = ({ search, next }) => {
  const result = next(search);
  const remembered = readFilterMemory();
  const out: ProcurementFilters = { ...result };
  for (const field of FILTER_FIELD_NAMES) {
    if (!(field in result)) copyField(out, remembered, field);
  }
  return out;
};

/** Record the filters of a URL the group landed on. Preloads are not visits. */
export function recordFilters(
  search: ProcurementFilters,
  preload: boolean,
): void {
  if (!preload) writeFilterMemory(filterFieldsOf(search));
}

function copyField<K extends keyof DemandFilterFields>(
  out: DemandFilterFields,
  from: DemandFilterFields,
  key: K,
): void {
  out[key] = from[key];
}

// ─── The record ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'procurement:active-filters:v1';

let fallback: DemandFilterFields = {};

export function readFilterMemory(): DemandFilterFields {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return fallback;
    const parsed = demandFilterFieldsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : {};
  } catch {
    return fallback;
  }
}

export function writeFilterMemory(next: DemandFilterFields): void {
  const clean = withoutEmpty(next);
  fallback = clean;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
  } catch {
    // Storage refused (private mode, quota): the in-memory copy still works
    // for the rest of this page's life.
  }
}

/**
 * Forget everything, storage and fallback alike. Called on logout and on an
 * organization switch: the ids belong to one user in one org, and a tab is
 * not a user.
 */
export function clearFilterMemory(): void {
  fallback = {};
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Drop undefined values and empty lists: neither is a filter. */
function withoutEmpty(fields: DemandFilterFields): DemandFilterFields {
  return Object.fromEntries(
    Object.entries(fields).filter(
      ([, value]) =>
        value !== undefined && !(Array.isArray(value) && value.length === 0),
    ),
  );
}

/**
 * The divisions a set of warehouses belong to, limited to divisions the board
 * offers. The forecast can filter to a branch without naming its division;
 * the board needs one.
 */
export function divisionsOfWarehouses(
  warehouseIds: string[],
  warehouses: { id: string; division?: { id: string } | null }[],
  divisions: { id: string }[],
): string[] {
  const divisionOf = new Map(warehouses.map((w) => [w.id, w.division?.id]));
  const offered = new Set(divisions.map((d) => d.id));
  const derived = new Set<string>();
  for (const id of warehouseIds) {
    const division = divisionOf.get(id);
    if (division && offered.has(division)) derived.add(division);
  }
  return [...derived];
}
